import type { DataImportSource, DataImportSourceType } from "@/schema/data-import-source"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  createDataImportSource,
  fanOutDataImportSource,
  FanOutResponse,
  submitTwoFactorCodeForSource,
  updateDataImportSourceCredentials,
  uploadDataImportSourceFile,
} from "./api"

export function useUploadDataImportSourceFile() {
  const getContext = useApiContext()

  return useMutation<string, Error, File>({
    mutationFn: async (file) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return uploadDataImportSourceFile(ctx, file)
    },
    meta: { errorToast: { fallback: "The Wealthbox export upload failed. Please select the file again." } },
  })
}

export function useCreateDataImportSource() {
  const getContext = useApiContext()
  const qc = useQueryClient()

  return useMutation<
    DataImportSource,
    Error,
    {
      sourceType: DataImportSourceType
      file: File | null
      bookId?: string | null
      importWorkflows?: boolean
      importOpportunities?: boolean
      importFileSignedId?: string
      crmUsername?: string
      crmPassword?: string
    }
  >({
    mutationFn: async ({
      sourceType,
      file,
      bookId,
      importWorkflows,
      importOpportunities,
      importFileSignedId,
      crmUsername,
      crmPassword,
    }) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return createDataImportSource(ctx, sourceType, file, getContext, bookId, {
        importWorkflows,
        importOpportunities,
        importFileSignedId,
        crmUsername,
        crmPassword,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["data-import-sources"] })
    },
  })
}

export function useSubmitTwoFactorCodeForSource(sourceId: string) {
  const getContext = useApiContext()
  const qc = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: async (code) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return submitTwoFactorCodeForSource(ctx, sourceId, code)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["data-import-sources"] })
    },
  })
}

export function useUpdateDataImportSourceCredentials(sourceId: string) {
  const getContext = useApiContext()
  const qc = useQueryClient()

  return useMutation<DataImportSource, Error, { crmUsername: string; crmPassword: string }>({
    mutationFn: async (credentials) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return updateDataImportSourceCredentials(ctx, sourceId, credentials)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["data-import-sources"] })
    },
  })
}

export function useFanOutDataImportSource() {
  const getContext = useApiContext()
  const qc = useQueryClient()

  return useMutation<FanOutResponse, Error, { sourceId: string }>({
    mutationFn: async ({ sourceId }) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return fanOutDataImportSource(ctx, sourceId)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["data-import-sources"] })
    },
  })
}
